import { getBouncySumsQuestionScore } from '@/features/bouncySums/utils/scoring';

import type { BouncySumsQuestion } from '@/features/bouncySums/types';
import type { Question } from '@/features/gameSession/types';

const getQuestionDisplayText = (question: Question) => {
	const display = question.question_display;

	if (typeof display === 'string') {
		return display
			.replace(/<[^>]+>/g, ' ')
			.replace(/\s+/g, ' ')
			.trim();
	}

	if (Array.isArray(display)) {
		const firstDisplay = display.find((item): item is string => typeof item === 'string');

		if (firstDisplay) {
			return firstDisplay
				.replace(/<[^>]+>/g, ' ')
				.replace(/\s+/g, ' ')
				.trim();
		}
	}

	return String(question.title ?? '').trim();
};

const getCorrectAnswerText = (question: Question) => {
	const correctAnswer =
		question.answers?.find((answer) => answer.is_correct || answer.correct) ?? question.answers?.[0];

	return String(correctAnswer?.answer_display ?? correctAnswer?.answer_text ?? correctAnswer?.content ?? '').trim();
};

const convertQuestion = (question: Question, index: number): BouncySumsQuestion | null => {
	const prompt = getQuestionDisplayText(question);
	const answer = getCorrectAnswerText(question).replace(/\D/g, '');
	const match = prompt.match(/(\d{1,2})\s*[xX*×]\s*(\d{1,2})\s*=?/);

	if (!match || !answer) {
		return null;
	}

	const firstNumber = Number(match[1]);
	const secondNumber = Number(match[2]);

	if (!Number.isFinite(firstNumber) || !Number.isFinite(secondNumber)) {
		return null;
	}

	return {
		id: String(question.id ?? `provided-${index}`),
		question: `${firstNumber}x${secondNumber}=`,
		answer,
		table: String(firstNumber),
		score: getBouncySumsQuestionScore(firstNumber, secondNumber),
		sourceQuestion: question,
	};
};

export const buildBouncySumsQuestions = (providedQuestions?: Question[]) => {
	const convertedQuestions = providedQuestions?.map(convertQuestion).filter(Boolean) as
		| BouncySumsQuestion[]
		| undefined;

	return convertedQuestions?.length ? convertedQuestions : [];
};

export const toGameSessionQuestion = (
	question: BouncySumsQuestion,
	questionIndex: number,
	topicTitle: string,
): Question => {
	if (question.sourceQuestion) {
		return question.sourceQuestion;
	}

	return {
		id: 880000 + questionIndex,
		ai_tutor_enabled: false,
		explanation_available: false,
		question_display: question.question,
		time_estimate_in_seconds: 0,
		title: question.question,
		topic_id: 0,
		topic_title: topicTitle,
		type: 'Question::OpenEnded',
		answers: [
			{
				id: question.id,
				is_correct: true,
				answer_display: question.answer,
			},
		],
	};
};
